import { lazy } from 'react';

import type { RouteConfig } from '../../types';

// Lazy load layouts
const DashboardLayout = lazy(() => import('@/layouts/DashboardLayout'));
const ProjectManagementLayout = lazy(() => import('@/pages/projectManagement/layout'));
const PMUpdateLayout = lazy(() => import('@/pages/projectManagement/[projectId]/update/layout'));

// Lazy load project management pages
const ProjectManagementPage = lazy(() => import('@/pages/projectManagement'));
const CreateProjectManagementPage = lazy(() => import('@/pages/projectManagement/create'));
const PMOverviewPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/overview'));
const PMDefinitionPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/definition'));
const PMInitiationPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/initiation'));
const PMExecutionPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/execution'));
const PMProductionPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/production'));
const PMProductionStatsPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/productionStats'));
const PMCloseoutPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/closeout'));
const PMCopFolderLayout = lazy(
  () => import('@/pages/projectManagement/[projectId]/update/closeout/cop/[folderId]/layout')
);
const PMCopFolderExplorerPage = lazy(
  () => import('@/pages/projectManagement/[projectId]/update/closeout/cop/[folderId]')
);
const PMEconomicsPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/economics'));
const PMRevenueForecastsPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/revenueForecasts'));
const PMLaborForecastsPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/laborForecasts'));
const PMOverallCostsPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/overallCosts'));
const PMScheduleExecutionPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/scheduleExecution'));
const PMMonthlyPLPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/monthlyPL'));
const PMFinancialSummaryPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/summary'));
const PMChangeOrdersPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/changeOrders'));
const PMDocumentsPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/documents'));
const PMLegalDocumentsPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/documents/legal'));
const PMOverallDocumentsPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/documents/overall'));
const PMCustomInvoicesPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/customInvoices'));
const PMTimeSheetsPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/timesheets'));
const PMManagementLaborPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/managementLabor'));
const PMInvoicesPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/invoices'));
const PMCommentsPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/comments'));
const PMHistoryPage = lazy(() => import('@/pages/projectManagement/[projectId]/update/history'));

/**
 * Project Management routes
 * Handles comprehensive project management with multiple update sections
 */
export const projectManagementRoutes: RouteConfig = {
  path: '/projectManagement',
  layout: DashboardLayout,
  children: [
    {
      path: '',
      layout: ProjectManagementLayout,
      element: ProjectManagementPage,
    },
    {
      path: '/create',
      layout: ProjectManagementLayout,
      element: CreateProjectManagementPage,
    },
    {
      path: '/:projectId/update',
      layout: ProjectManagementLayout,
      children: [
        {
          path: '/overview',
          layout: PMUpdateLayout,
          element: PMOverviewPage,
        },
        {
          path: '/definition',
          layout: PMUpdateLayout,
          element: PMDefinitionPage,
        },
        {
          path: '/initiation',
          layout: PMUpdateLayout,
          element: PMInitiationPage,
        },
        {
          path: '/execution',
          layout: PMUpdateLayout,
          element: PMExecutionPage,
        },
        {
          path: '/production',
          layout: PMUpdateLayout,
          element: PMProductionPage,
        },
        {
          path: '/productionStats',
          layout: PMUpdateLayout,
          element: PMProductionStatsPage,
        },
        {
          path: '/closeout',
          layout: PMUpdateLayout,
          element: PMCloseoutPage,
        },
        {
          path: '/closeout/cop/:folderId',
          layout: PMUpdateLayout,
          children: [
            {
              path: '',
              layout: PMCopFolderLayout,
              element: PMCopFolderExplorerPage,
            },
          ],
        },
        {
          path: '/economics',
          layout: PMUpdateLayout,
          element: PMEconomicsPage,
        },
        {
          path: '/revenueForecasts',
          layout: PMUpdateLayout,
          element: PMRevenueForecastsPage,
        },
        {
          path: '/laborForecasts',
          layout: PMUpdateLayout,
          element: PMLaborForecastsPage,
        },
        {
          path: '/overallCosts',
          layout: PMUpdateLayout,
          element: PMOverallCostsPage,
        },
        {
          path: '/scheduleExecution',
          layout: PMUpdateLayout,
          element: PMScheduleExecutionPage,
        },
        {
          path: '/monthlyPL',
          layout: PMUpdateLayout,
          element: PMMonthlyPLPage,
        },
        {
          path: '/summary',
          layout: PMUpdateLayout,
          element: PMFinancialSummaryPage,
        },
        {
          path: '/changeOrders',
          layout: PMUpdateLayout,
          element: PMChangeOrdersPage,
        },
        {
          path: '/documents',
          layout: PMUpdateLayout,
          element: PMDocumentsPage,
        },
        {
          path: '/documents/legal',
          layout: PMUpdateLayout,
          element: PMLegalDocumentsPage,
        },
        {
          path: '/documents/overall',
          layout: PMUpdateLayout,
          element: PMOverallDocumentsPage,
        },
        {
          path: '/customInvoices',
          layout: PMUpdateLayout,
          element: PMCustomInvoicesPage,
        },
        {
          path: '/timesheets',
          layout: PMUpdateLayout,
          element: PMTimeSheetsPage,
        },
        {
          path: '/managementLabor',
          layout: PMUpdateLayout,
          element: PMManagementLaborPage,
        },
        {
          path: '/invoices',
          layout: PMUpdateLayout,
          element: PMInvoicesPage,
        },
        {
          path: '/comments',
          layout: PMUpdateLayout,
          element: PMCommentsPage,
        },
        {
          path: '/history',
          layout: PMUpdateLayout,
          element: PMHistoryPage,
        },
      ],
    },
  ],
};
